import { TAGHIGHLIGHT_BACKDROP_HEIGHT } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import React, { useRef, useEffect } from "react";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import {
  loadingSubtimelineBackgroundColor,
  subTimelineLoadingDashColor,
} from "./skuOperatorTimelineUtils";

interface RejectCountCanvasProps {
  width: number;
  isTimelineExpanded: boolean;
  rejectCountSegments: {
    rejectOutput: number | null;
    range: { ge: number; lt?: number };
  }[];
  processId: string;
}

/**
 * Helper function to draw loading indicator pattern
 */
const drawLoadingIndicator = (
  ctx: CanvasRenderingContext2D,
  x: number,
  width: number,
  height: number
) => {
  const middleY = height / 2;
  const dashColor = subTimelineLoadingDashColor;

  // Save current context state
  ctx.save();

  // Draw a dashed line across the middle of the area
  ctx.strokeStyle = dashColor;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]); // Dashed line pattern

  // Draw the horizontal dashed line
  ctx.beginPath();
  ctx.moveTo(x, middleY);
  ctx.lineTo(x + width, middleY);
  ctx.stroke();

  // Reset line dash and restore context
  ctx.setLineDash([]);
  ctx.restore();
};

export const RejectCountCanvas: React.FC<RejectCountCanvasProps> = ({
  processId,
  width,
  isTimelineExpanded,
  rejectCountSegments,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { scaleTime } = useTimeAxisContext();

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = TAGHIGHLIGHT_BACKDROP_HEIGHT;

    // Get current time position on the canvas
    const nowX = scaleTime(Date.now());

    // draw backdrop grey rectangle using loading background color
    ctx.fillStyle = loadingSubtimelineBackgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the reject count segments
    if (isTimelineExpanded) {
      // If there are no segments or the segments array is empty, show the entire area as loading
      // but only up to the current time
      if (!rejectCountSegments || rejectCountSegments.length === 0) {
        // Create a loading indicator for the entire visible area up to now
        const loadingWidth = Math.min(nowX, canvas.width);
        drawLoadingIndicator(
          ctx,
          0,
          loadingWidth,
          TAGHIGHLIGHT_BACKDROP_HEIGHT
        );
      } else {
        // Sort segments by start time for gap detection
        const sortedSegments = [...rejectCountSegments].sort(
          (a, b) => a.range.ge - b.range.ge
        );

        // Check for gaps between segments and draw loading indicators in those gaps
        if (sortedSegments.length > 1) {
          for (let i = 0; i < sortedSegments.length - 1; i++) {
            const currentSegment = sortedSegments[i];
            const nextSegment = sortedSegments[i + 1];

            // Check if there's a gap between this segment and the next
            if (
              currentSegment.range.lt &&
              currentSegment.range.lt < nextSegment.range.ge
            ) {
              // There's a gap - draw loading indicator in the gap
              const gapStartX = scaleTime(currentSegment.range.lt) || 0;
              // Ensure we don't draw loading indicators beyond current time
              const gapEndX = Math.min(
                scaleTime(nextSegment.range.ge) || 0,
                nowX
              );
              const gapWidth = Math.max(2, gapEndX - gapStartX);

              // Only draw if gap is visible on canvas
              if (gapWidth > 0 && gapStartX < canvas.width && gapEndX > 0) {
                // Fill gap with loading background
                ctx.fillStyle = loadingSubtimelineBackgroundColor;
                ctx.fillRect(
                  gapStartX,
                  0,
                  gapWidth,
                  TAGHIGHLIGHT_BACKDROP_HEIGHT
                );

                // Add loading indicator
                drawLoadingIndicator(
                  ctx,
                  gapStartX,
                  gapWidth,
                  TAGHIGHLIGHT_BACKDROP_HEIGHT
                );
              }
            }
          }

          // Check if there's a gap at the beginning (before first segment)
          const firstSegment = sortedSegments[0];
          if (firstSegment && firstSegment.range?.ge > 0) {
            const firstSegmentStart = scaleTime(firstSegment.range.ge) || 0;
            if (firstSegmentStart > 0) {
              // Draw loading indicator from 0 to first segment
              ctx.fillStyle = loadingSubtimelineBackgroundColor;
              ctx.fillRect(
                0,
                0,
                firstSegmentStart,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );
              drawLoadingIndicator(
                ctx,
                0,
                firstSegmentStart,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );
            }
          }

          // Check if there's a gap at the end (after last segment)
          const lastSegment = sortedSegments[sortedSegments.length - 1];
          if (lastSegment && lastSegment.range?.lt) {
            const lastSegmentEnd =
              scaleTime(lastSegment.range.lt) || canvas.width;
            // Only draw loading indicator if the last segment ends before current time
            // and before the end of the canvas
            if (lastSegmentEnd < nowX && lastSegmentEnd < canvas.width) {
              // Draw loading indicator from last segment to current time or canvas width
              const endX = Math.min(nowX, canvas.width);
              const endGapWidth = endX - lastSegmentEnd;
              ctx.fillStyle = loadingSubtimelineBackgroundColor;
              ctx.fillRect(
                lastSegmentEnd,
                0,
                endGapWidth,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );
              drawLoadingIndicator(
                ctx,
                lastSegmentEnd,
                endGapWidth,
                TAGHIGHLIGHT_BACKDROP_HEIGHT
              );
            }
          }
        }
      }

      // Draw the reject count segments over the loading indicators
      if (rejectCountSegments && rejectCountSegments.length > 0) {
        for (const segment of rejectCountSegments) {
          if (!segment.range?.ge) {
            continue;
          }

          const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : 0;
          const x1 = segment.range.lt
            ? scaleTime(segment.range.lt) || 0
            : scaleTime(Date.now());

          // Draw bars for reject count
          if (
            segment.rejectOutput !== null &&
            segment.rejectOutput !== undefined
          ) {
            const segmentWidth = x1 - x0;

            if (segment.rejectOutput > 0) {
              // Red color for positive reject counts
              ctx.fillStyle = "#f76c5e";
            } else if (segment.rejectOutput < 0) {
              // Light-red color for negative reject counts
              ctx.fillStyle = "#ffcccc";
            } else {
              // Skip if reject count is exactly 0
              continue;
            }

            ctx.fillRect(x0, 0, segmentWidth, TAGHIGHLIGHT_BACKDROP_HEIGHT);

            // Add reject count value in the middle if segment is wide enough
            const text = `${segment.rejectOutput.toFixed(0)}`;
            if (segmentWidth > text.length * 6) {
              ctx.fillStyle = "#fff";
              ctx.font = "bold 12px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(
                text,
                x0 + segmentWidth / 2,
                TAGHIGHLIGHT_BACKDROP_HEIGHT / 2 + 1
              );
            }
          }
        }
      }
    }
  }, [isTimelineExpanded, rejectCountSegments, scaleTime, width]);

  return (
    <div
      style={{
        position: "relative",
        width: `${width}px`,
        height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
      }}
    >
      <canvas
        ref={canvasRef}
        id={`reject-count-canvas-${processId}`}
        className={"reject-count-canvas"}
        width={`${width}px`}
        height={`${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`}
        style={{
          width: `${width}px`,
          height: `${TAGHIGHLIGHT_BACKDROP_HEIGHT}px`,
          cursor: "pointer",
          opacity: isTimelineExpanded ? 1 : 0,
          transition: `opacity 0.5s ease-in-out 1s`,
          pointerEvents: isTimelineExpanded ? "auto" : "none",
          position: "relative",
          zIndex: 1,
        }}
      />
    </div>
  );
};
